import React, { useEffect, useMemo, useState } from "react";
import { Bell, CheckCircle2, Loader2, Mail, Moon, Send } from "lucide-react";

import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import {
  accountSettingsFieldDescriptionClassName,
  accountSettingsNestedCardClassName,
} from "../accountSettingsCardShell";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldClassName,
  accountSettingsFieldLabelStackClassName,
  accountSettingsFieldStackClassName,
} from "./SettingCard";
import {
  NotificationEventListHeader,
  NotificationEventPreferenceRow,
} from "./NotificationEventPreferenceRow";
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../services/notificationsApi";
import { NotificationPreferences } from "../../../../types/notifications";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import { toast } from "sonner";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { ConnectTelegramButton } from "../../LiveStrategies/ConnectTelegramButton";
import { useTelegramLinked } from "../../LiveStrategies/useTelegramLinked";
import {
  NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_PREFERENCE_EVENT_KEYS,
  type NotificationPreferenceEventKey,
} from "@/constants/notificationEventLabels";

/** Rows inside one bordered list, separated by hairlines (explicit: the global reset strips borders). */
const NOTIFICATION_LIST_ROWS_CLASS =
  "[&>*+*]:!border-t [&>*+*]:!border-solid [&>*+*]:!border-border";

/** Launchpad rows: event + one Telegram column (64px, like In-app / Email elsewhere). */
const LAUNCHPAD_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_64px] items-start gap-3 px-4 py-3.5";

const SUPPORTED_EVENT_KEYS = NOTIFICATION_PREFERENCE_EVENT_KEYS;

const SYSTEM_EVENT_KEYS = new Set<NotificationPreferenceEventKey>([
  "system.broker_disconnected",
  "system.broker_reconnected",
  "system.sync_failed",
  "system.sync_recovered",
]);

const PERFORMANCE_EVENT_KEYS = [
  "performance.daily.new_best_day",
  "performance.daily.new_worst_day",
  "performance.pnl.milestone_crossed",
  "performance.summary.daily",
  "performance.summary.weekly",
  "performance.summary.monthly",
] as const satisfies ReadonlyArray<NotificationPreferenceEventKey>;

const SYSTEM_EVENT_KEYS_LIST = [
  "system.broker_disconnected",
  "system.broker_reconnected",
  "system.sync_failed",
  "system.sync_recovered",
] as const satisfies ReadonlyArray<NotificationPreferenceEventKey>;

/** In-app display titles for each supported notification event key */
const eventLabels = Object.fromEntries(
  SUPPORTED_EVENT_KEYS.map((key) => [key, NOTIFICATION_EVENT_LABELS[key]])
) as Record<NotificationPreferenceEventKey, string>;

type SupportedEventKey = NotificationPreferenceEventKey;

/** Launchpad Telegram settings that aren't an event (quiet hours). */
const LAUNCHPAD_SETTINGS_KEY = "launchpad.telegram";

/** Launchpad events sent to Telegram (the backend reads the `telegram` flag of each). */
const LAUNCHPAD_TELEGRAM_EVENTS = [
  {
    key: "launchpad.signal",
    title: "Signals",
    description: "Each new signal from a running strategy — paper trades, orders sent, skips.",
  },
  {
    key: "launchpad.signal.pending",
    title: "Approval requests",
    description: "Co-pilot signals waiting for you, with Approve / Reject buttons.",
  },
  {
    key: "launchpad.trade",
    title: "Trade updates",
    description: "A message when a trade fills, closes (with R and P&L), takes a partial or moves its stop — with Close / break-even buttons.",
  },
  {
    key: "launchpad.alert",
    title: "Safety alerts",
    description: "When something needs you: errors, late checks, the kill switch, the daily loss limit, broker problems. Sent even in quiet hours.",
  },
  {
    key: "launchpad.summary.daily",
    title: "Daily summary",
    description: "One message each evening: signals, closed trades and P&L per strategy.",
  },
] as const;

type ChannelTone = "on" | "off" | "pending";

const CHANNEL_PILL: Record<ChannelTone, string> = {
  on: "bg-[color:var(--brand-tropical-surf)]/12 text-[color:var(--brand-tropical-surf)]",
  off: "bg-muted text-muted-foreground",
  pending: "bg-amber-500/12 text-amber-600 dark:text-amber-400",
};

/** One delivery channel row: icon, name + status pill, one-line description, control on the right. */
function ChannelRow({
  icon,
  name,
  detail,
  tone,
  status,
  control,
}: {
  icon: React.ReactNode;
  name: string;
  detail: string;
  tone: ChannelTone;
  status: string;
  control: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3.5"
      data-notification-channel={name.toLowerCase()}
    >
      <span
        className={cn(
          "flex size-9 items-center justify-center rounded-full",
          tone === "on"
            ? "bg-[color:var(--brand-tropical-surf)]/12 text-[color:var(--brand-tropical-surf)]"
            : "bg-muted text-muted-foreground"
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className={BROKER_ALERT_TITLE_CLASS}>{name}</span>
          <span className={cn("rounded-full px-2 py-px text-[10px] font-medium uppercase tracking-wide", CHANNEL_PILL[tone])}>
            {status}
          </span>
        </span>
        <span className={cn(accountSettingsFieldDescriptionClassName, "truncate")}>{detail}</span>
      </span>
      <span className="flex items-center justify-end">{control}</span>
    </div>
  );
}

const createDefaultPreferences = (): NotificationPreferences => ({
  inAppEnabled: true,
  emailEnabled: true,
  displayCurrency: "USD",
  supportedCurrencies: [],
  perCategory: {},
  perEvent: Object.fromEntries(
    SUPPORTED_EVENT_KEYS.map((key) => [
      key,
      {
        inApp: true,
        // System notifications are not supported via email
        email: SYSTEM_EVENT_KEYS.has(key) ? false : true,
        accountScope: { mode: "all", accountCodes: [] },
      },
    ])
  ),
});

/** Compare only fields edited on this page (globals + supported per-event prefs). */
function notificationPreferencesDirtySnapshot(prefs: NotificationPreferences): string {
  return JSON.stringify({
    inAppEnabled: prefs.inAppEnabled,
    emailEnabled: prefs.emailEnabled,
    launchpadTelegram: LAUNCHPAD_TELEGRAM_EVENTS.map(({ key }) => prefs.perEvent[key]?.telegram ?? true),
    launchpadQuiet: prefs.perEvent[LAUNCHPAD_SETTINGS_KEY]?.quiet ?? null,
    perEvent: Object.fromEntries(
      SUPPORTED_EVENT_KEYS.map((key) => {
        const event = prefs.perEvent[key] ?? {};
        return [
          key,
          {
            inApp: event.inApp ?? true,
            email: SYSTEM_EVENT_KEYS.has(key) ? false : (event.email ?? true),
            accountScope: {
              mode: event.accountScope?.mode ?? "all",
              accountCodes: [...(event.accountScope?.accountCodes ?? [])].sort(),
            },
          },
        ];
      })
    ),
  });
}

function mergeWithDefaults(loaded: NotificationPreferences): NotificationPreferences {
  const defaults = createDefaultPreferences();
  const mergedPerEvent = { ...defaults.perEvent, ...(loaded.perEvent ?? {}) };
  for (const key of SUPPORTED_EVENT_KEYS) {
    const current = mergedPerEvent[key] ?? {};
    mergedPerEvent[key] = {
      inApp: current.inApp ?? true,
      // System notifications are not supported via email
      email: SYSTEM_EVENT_KEYS.has(key) ? false : (current.email ?? true),
      accountScope: {
        mode: current.accountScope?.mode ?? "all",
        accountCodes: current.accountScope?.accountCodes ?? [],
      },
    };
  }
  return {
    inAppEnabled: loaded.inAppEnabled ?? true,
    emailEnabled: loaded.emailEnabled ?? true,
    telegramEnabled: loaded.telegramEnabled,
    displayCurrency: (loaded.displayCurrency || "USD").toUpperCase(),
    supportedCurrencies: loaded.supportedCurrencies ?? [],
    perCategory: loaded.perCategory ?? {},
    perEvent: mergedPerEvent,
  };
}

const NotificationSettings: React.FC = () => {
  const { isAdmin } = useFeatureFlags();
  const { linked: telegramLinked, refresh: refreshTelegram } = useTelegramLinked();
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [savedPrefsSnapshot, setSavedPrefsSnapshot] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedScopeErrorEvent, setSelectedScopeErrorEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { accounts, loading: accountsLoading } = useActiveBrokerAccount();

  const accountCodeOptions = useMemo(
    () => accounts.map((account: { accountCode: string }) => account.accountCode),
    [accounts]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!prefs || savedPrefsSnapshot === null) return false;
    return notificationPreferencesDirtySnapshot(prefs) !== savedPrefsSnapshot;
  }, [prefs, savedPrefsSnapshot]);

  const commitSavedPrefs = (next: NotificationPreferences) => {
    const merged = mergeWithDefaults(next);
    setPrefs(merged);
    setSavedPrefsSnapshot(notificationPreferencesDirtySnapshot(merged));
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await fetchNotificationPreferences();
        if (isMounted) {
          commitSavedPrefs(data);
        }
      } catch (error) {
        console.error("Failed to load notification preferences:", error);
        if (isMounted) {
          commitSavedPrefs(createDefaultPreferences());
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggleGlobal = (key: "inAppEnabled" | "emailEnabled") => {
    if (!prefs) return;
    const nextEnabled = !prefs[key];

    // Behavior:
    // - Turning a global channel OFF disables it everywhere (sets per-event channel false).
    // - Turning a global channel ON does NOT force-enable all events.
    if (key === "emailEnabled") {
      if (!nextEnabled) {
        const nextPerEvent = { ...prefs.perEvent };
        for (const eventKey of SUPPORTED_EVENT_KEYS) {
          const current = nextPerEvent[eventKey] ?? {
            inApp: true,
            email: true,
            accountScope: { mode: "all", accountCodes: [] },
          };
          nextPerEvent[eventKey] = {
            ...current,
            email: SYSTEM_EVENT_KEYS.has(eventKey) ? false : false,
          };
        }
        setPrefs({ ...prefs, emailEnabled: false, perEvent: nextPerEvent });
        return;
      }
      setPrefs({ ...prefs, emailEnabled: true });
      return;
    }

    if (key === "inAppEnabled") {
      if (!nextEnabled) {
        const nextPerEvent = { ...prefs.perEvent };
        for (const eventKey of SUPPORTED_EVENT_KEYS) {
          const current = nextPerEvent[eventKey] ?? {
            inApp: true,
            email: true,
            accountScope: { mode: "all", accountCodes: [] },
          };
          nextPerEvent[eventKey] = {
            ...current,
            inApp: false,
          };
        }
        setPrefs({ ...prefs, inAppEnabled: false, perEvent: nextPerEvent });
        return;
      }
      setPrefs({ ...prefs, inAppEnabled: true });
      return;
    }
  };

  /** Quiet hours: on with 22:00–07:00 in the browser's zone, off clears them (null). */
  const setLaunchpadQuiet = (quiet: { start: string; end: string; tz: string } | null) => {
    if (!prefs) return;
    const current = prefs.perEvent[LAUNCHPAD_SETTINGS_KEY] ?? {};
    setPrefs({ ...prefs, perEvent: { ...prefs.perEvent, [LAUNCHPAD_SETTINGS_KEY]: { ...current, quiet } } });
  };

  const handleToggleLaunchpadTelegram = (eventKey: string) => {
    if (!prefs) return;
    const current = prefs.perEvent[eventKey] ?? {};
    setPrefs({
      ...prefs,
      perEvent: { ...prefs.perEvent, [eventKey]: { ...current, telegram: !(current.telegram ?? true) } },
    });
  };

  const handleToggleEvent = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    field: "inApp" | "email"
  ) => {
    if (!prefs) return;
    if (field === "email" && SYSTEM_EVENT_KEYS.has(eventKey)) return;
    const current = prefs.perEvent[eventKey] || {
      inApp: true,
      email: true,
      accountScope: { mode: "all", accountCodes: [] },
    };

    // If global channel is OFF and user enables a single event, auto-enable global channel
    // without changing other events.
    const nextGlobalPatch: Partial<NotificationPreferences> = {};
    if (field === "email" && !prefs.emailEnabled && current.email === false) {
      nextGlobalPatch.emailEnabled = true;
    }
    if (field === "inApp" && !prefs.inAppEnabled && current.inApp === false) {
      nextGlobalPatch.inAppEnabled = true;
    }

    setPrefs({
      ...prefs,
      ...nextGlobalPatch,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          [field]: !current[field],
        },
      },
    });
  };

  const handleScopeModeChange = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    mode: "all" | "selected"
  ) => {
    if (!prefs) return;
    const current = prefs.perEvent[eventKey] || {};
    setSelectedScopeErrorEvent((prev) => (prev === eventKey ? null : prev));
    setPrefs({
      ...prefs,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          accountScope: {
            mode,
            accountCodes: current.accountScope?.accountCodes ?? [],
          },
        },
      },
    });
  };

  const handleScopeAccountsChange = (
    eventKey: (typeof SUPPORTED_EVENT_KEYS)[number],
    accountCodes: string[]
  ) => {
    if (!prefs) return;
    const current = prefs.perEvent[eventKey] || {};
    setSelectedScopeErrorEvent((prev) => (prev === eventKey ? null : prev));
    setPrefs({
      ...prefs,
      perEvent: {
        ...prefs.perEvent,
        [eventKey]: {
          ...current,
          accountScope: {
            mode: current.accountScope?.mode ?? "selected",
            accountCodes,
          },
        },
      },
    });
  };

  const handleSave = async () => {
    if (!prefs) return;
    for (const eventKey of SUPPORTED_EVENT_KEYS) {
      const eventPref = prefs.perEvent[eventKey];
      if (
        eventPref?.accountScope?.mode === "selected" &&
        (!eventPref.accountScope.accountCodes || eventPref.accountScope.accountCodes.length === 0)
      ) {
        setSelectedScopeErrorEvent(eventKey);
        toastError("Select at least one account", { description: `${eventLabels[eventKey]} is set to selected accounts.` });
        return;
      }
    }

    setSaving(true);
    try {
      const updated = await updateNotificationPreferences(prefs);
      commitSavedPrefs(updated);
      toast.success("Notification preferences saved");
    } catch (error) {
      console.error("Failed to save notification preferences:", error);
      presentMutationError(error, "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
      </div>
    );
  }

  const renderEventPreferences = (
    eventKeys: readonly SupportedEventKey[],
    options?: { showEmailToggle?: boolean }
  ) => (
    <div className={cn(accountSettingsNestedCardClassName, NOTIFICATION_LIST_ROWS_CLASS)}>
      <NotificationEventListHeader />
      {eventKeys.map((key) => {
        const current = prefs.perEvent[key] ?? {
          inApp: true,
          email: SYSTEM_EVENT_KEYS.has(key) ? false : true,
          accountScope: { mode: "all", accountCodes: [] },
        };
        const selectedCodes = current.accountScope?.accountCodes ?? [];
        const mode = current.accountScope?.mode ?? "all";
        const hasScopeError =
          selectedScopeErrorEvent === key && mode === "selected" && selectedCodes.length === 0;
        const showEmailToggle = options?.showEmailToggle ?? !SYSTEM_EVENT_KEYS.has(key);

        return (
          <NotificationEventPreferenceRow
            key={key}
            eventKey={key}
            title={eventLabels[key]}
            inApp={current.inApp ?? true}
            email={current.email ?? !SYSTEM_EVENT_KEYS.has(key)}
            showEmailToggle={showEmailToggle}
            mode={mode}
            selectedCodes={selectedCodes}
            hasScopeError={hasScopeError}
            accountsLoading={accountsLoading}
            accountCodeOptions={accountCodeOptions}
            onToggleInApp={() => handleToggleEvent(key, "inApp")}
            onToggleEmail={() => handleToggleEvent(key, "email")}
            onScopeModeChange={(nextMode) => handleScopeModeChange(key, nextMode)}
            onScopeAccountsChange={(codes) => handleScopeAccountsChange(key, codes)}
          />
        );
      })}
    </div>
  );

  const anyLaunchpadTelegramOn = LAUNCHPAD_TELEGRAM_EVENTS.some(({ key }) => prefs.perEvent[key]?.telegram ?? true);

  return (
    <div
      className={accountSettingsCardStackClassName}
      data-notification-settings=""
      data-account-settings-card-stack=""
    >
      {/* 1 — Where Tessera can reach you. */}
      <SettingCard
        title="Channels"
        description="Where Tessera can reach you. Turning a channel off silences every event on it."
      >
        <div
          className={cn(accountSettingsNestedCardClassName, NOTIFICATION_LIST_ROWS_CLASS)}
          data-notification-channels=""
        >
          <ChannelRow
            icon={<Bell className="size-4" aria-hidden />}
            name="In-app"
            detail="The notifications centre and sidebar."
            tone={prefs.inAppEnabled ? "on" : "off"}
            status={prefs.inAppEnabled ? "On" : "Off"}
            control={
              <Switch
                checked={prefs.inAppEnabled}
                onCheckedChange={() => handleToggleGlobal("inAppEnabled")}
                aria-label="In-app notifications"
              />
            }
          />
          <ChannelRow
            icon={<Mail className="size-4" aria-hidden />}
            name="Email"
            detail="Summaries and performance alerts in your inbox."
            tone={prefs.emailEnabled ? "on" : "off"}
            status={prefs.emailEnabled ? "On" : "Off"}
            control={
              <Switch
                checked={prefs.emailEnabled}
                onCheckedChange={() => handleToggleGlobal("emailEnabled")}
                aria-label="Email notifications"
              />
            }
          />
          {isAdmin ? (
            <ChannelRow
              icon={<Send className="size-4" aria-hidden />}
              name="Telegram"
              detail={
                telegramLinked
                  ? "Launchpad signals and one-tap approvals in your Tessera bot chat."
                  : "Launchpad signals and one-tap approvals on your phone."
              }
              tone={telegramLinked ? (anyLaunchpadTelegramOn ? "on" : "off") : "pending"}
              status={telegramLinked == null ? "Checking" : telegramLinked ? "Connected" : "Not connected"}
              control={
                telegramLinked === false ? (
                  <ConnectTelegramButton onReturn={refreshTelegram} size="sm" variant="gradient" label="Connect" />
                ) : telegramLinked ? (
                  <CheckCircle2 className="size-5 text-[color:var(--brand-tropical-surf)]" aria-label="Connected" />
                ) : null
              }
            />
          ) : null}
        </div>
      </SettingCard>

      {/* 2 — What each product area sends, and on which channel. */}
      <SettingCard
        title="What you're notified about"
        description="Pick the events you want, per area — and which channel and accounts each one uses."
      >
        <Tabs defaultValue="performance" data-market-context-buckets-tabs="" className="w-full">
          <div className="w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="min-w-max">
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="broker">Broker &amp; sync</TabsTrigger>
              {isAdmin ? <TabsTrigger value="launchpad">Launchpad</TabsTrigger> : null}
            </TabsList>
          </div>
          <TabsContent value="performance" className="mt-0 pt-4">
            {renderEventPreferences(PERFORMANCE_EVENT_KEYS)}
          </TabsContent>
          <TabsContent value="broker" className="mt-0 pt-4">
            {renderEventPreferences(SYSTEM_EVENT_KEYS_LIST, { showEmailToggle: false })}
          </TabsContent>
          {isAdmin ? (
            <TabsContent value="launchpad" className="mt-0 pt-4">
              {telegramLinked === false ? (
                <p className="mb-3 flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <Send className="size-3.5 shrink-0" aria-hidden />
                  These are sent to Telegram — connect it under Channels above to receive them.
                </p>
              ) : null}
              <div className={cn(accountSettingsNestedCardClassName, NOTIFICATION_LIST_ROWS_CLASS)}>
                {/* Same table as the other tabs: event on the left, one column per channel. */}
                <div className={cn(LAUNCHPAD_GRID_CLASS, "py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground")}>
                  <span>Event</span>
                  <span className="text-center">Telegram</span>
                </div>
                {LAUNCHPAD_TELEGRAM_EVENTS.map(({ key, title, description }) => (
                  <div key={key} className={cn(LAUNCHPAD_GRID_CLASS, telegramLinked === false && "opacity-60")}>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className={BROKER_ALERT_TITLE_CLASS}>{title}</span>
                      <span className={accountSettingsFieldDescriptionClassName}>{description}</span>
                    </span>
                    <span className="flex justify-center pt-0.5">
                      <Switch
                        checked={prefs.perEvent[key]?.telegram ?? true}
                        onCheckedChange={() => handleToggleLaunchpadTelegram(key)}
                        aria-label={`${title} on Telegram`}
                      />
                    </span>
                  </div>
                ))}
                {(() => {
                  const quiet = prefs.perEvent[LAUNCHPAD_SETTINGS_KEY]?.quiet ?? null;
                  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
                  return (
                    <div className={cn(LAUNCHPAD_GRID_CLASS, telegramLinked === false && "opacity-60")} data-launchpad-quiet="">
                      <span className="flex min-w-0 flex-col gap-1.5">
                        <span className={cn(BROKER_ALERT_TITLE_CLASS, "flex items-center gap-1.5")}>
                          <Moon className="size-3.5" aria-hidden /> Quiet hours
                        </span>
                        <span className={accountSettingsFieldDescriptionClassName}>
                          New signals and trade updates are held and sent as one message when quiet hours end.
                          Approval requests and safety alerts still come straight away.
                        </span>
                        {quiet ? (
                          <span className="flex flex-wrap items-center gap-2 pt-1">
                            <Input
                              type="time"
                              className="w-28"
                              value={quiet.start}
                              onChange={(e) => setLaunchpadQuiet({ ...quiet, start: e.target.value || quiet.start })}
                              aria-label="Quiet hours start"
                            />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input
                              type="time"
                              className="w-28"
                              value={quiet.end}
                              onChange={(e) => setLaunchpadQuiet({ ...quiet, end: e.target.value || quiet.end })}
                              aria-label="Quiet hours end"
                            />
                            <span className="text-xs text-muted-foreground">{quiet.tz}</span>
                          </span>
                        ) : null}
                      </span>
                      <span className="flex justify-center pt-0.5">
                        <Switch
                          checked={quiet != null}
                          onCheckedChange={(on) => setLaunchpadQuiet(on ? { start: "22:00", end: "07:00", tz: zone } : null)}
                          aria-label="Quiet hours"
                        />
                      </span>
                    </div>
                  );
                })()}
              </div>
            </TabsContent>
          ) : null}
        </Tabs>
      </SettingCard>

      {hasUnsavedChanges ? (
        <div className="sticky bottom-4 z-10 flex items-center justify-end gap-3 rounded-lg border border-solid border-border bg-card/95 px-4 py-3 shadow-md backdrop-blur">
          <span className="mr-auto text-xs text-muted-foreground">You have unsaved changes.</span>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            disabled={saving}
            aria-busy={saving || undefined}
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            Save preferences
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationSettings;

